<template>
  <div>
    <div class="page-header d-flex align-items-center">

      <!-- Left: home -->
      <div class="header-section header-left">
        <img v-if="logoSrc" :src="'/images/'+logoSrc" v-bind:alt="currentCompany.name" height="36" class="me-2">
        <div class="logo" @click="goHome">
          <font-awesome-icon icon="home" class="home-icon" />
        </div>
      </div>

      <!-- Center: gear menu -->
      <div class="header-section header-center">
        <div v-if="[1,2,3,4].includes(client.role)" class="dropdown">
          <button class="btn btn-link header-btn dropdown-toggle" type="button" id="mainMenu" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
            <font-awesome-icon icon="gear" />
          </button>
          <div class="dropdown-menu" aria-labelledby="mainMenu">
            <button v-if="[4].includes(client.role)" class="dropdown-item" type="button" @click="organizationManagement">{{ $t('header.mainMenu.organizations') }}</button>
            <button v-if="[3,4].includes(client.role)" class="dropdown-item" type="button" @click="facilityManagement">{{ $t('header.mainMenu.facilities') }}</button>
            <button v-if="[3,4].includes(client.role)" class="dropdown-item" type="button" @click="userManagement">{{ $t('header.mainMenu.users') }}</button>
            <button v-if="[4].includes(client.role)" class="dropdown-item" type="button" @click="collectionAddressManagement">{{ $t('header.mainMenu.collectionAddresses') }}</button>
            <button v-if="[4].includes(client.role)" class="dropdown-item" type="button" @click="anomaliesView">{{ $t('header.mainMenu.anomalies') }}</button>
            <button v-if="[4].includes(client.role)" class="dropdown-item" type="button" @click="apiTokenManagement">{{ $t('header.mainMenu.apiTokens') }}</button>
            <button v-if="[4].includes(client.role)" class="dropdown-item" type="button" @click="emissionCoverageView">{{ $t('header.mainMenu.emissionCoverage') }}</button>
          </div>
        </div>
      </div>

      <!-- Right: client/profile -->
      <div class="header-section header-right">
        <div class="dropdown">
          <button class="btn btn-link header-btn dropdown-toggle" type="button" id="profileMenu" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
            <font-awesome-icon icon="user" class="me-1" />{{ client.name }} / {{ client.organization.code }}
          </button>
          <div class="dropdown-menu dropdown-menu-end" aria-labelledby="profileMenu">
            <button v-for="(organization, index) in client.organizations"
                    type="button"
                    @click="selectOrganization(organization)"
                    class="dropdown-item" :class="{'selected-menu' : organization.code === client.organization.code}"
                    :key="index"
                  >
              {{ organization.name }}
            </button>
            <button class="dropdown-item" style="color: white;" type="button">... ... ...</button>
            <button v-if="showWebPushToggle" class="dropdown-item" type="button" :disabled="webPushState === 'denied' || webPushBusy" @click="toggleWebPush()">
              <template v-if="webPushState === 'denied'">{{ $t('header.webPushDenied') }}</template>
              <template v-else-if="webPushState === 'subscribed'">✓ {{ $t('header.webPushDisable') }}</template>
              <template v-else>{{ $t('header.webPushEnable') }}</template>
            </button>
            <button class="dropdown-item" type="button" @click="signOut()">{{ $t('header.signOut') }}</button>
          </div>
        </div>
      </div>

    </div>
    <auto-logout />
  </div>
</template>

<script>
import AutoLogout from '@/components/AutoLogout.vue'
import LogoutViewMixin from '@/mixins/LogoutViewMixin.js'
import { mapGetters } from 'vuex'
import usersService from '@/services/users'
import * as webPush from '@/utils/web-push'

export default {
  name: 'PageHeader',
  mixins: [LogoutViewMixin],
  components: {
    'auto-logout': AutoLogout
  },
  data () {
    return {
      errorMessage: '',
      currentCompany: { logo: '', logoDark: '', name: '', short: '', backendMode: true, build: '' },
      webPushState: 'unsupported',
      webPushBusy: false
    }
  },
  computed: {
    ...mapGetters([
      'client',
      'theme'
    ]),
    logoSrc () {
      if (this.theme === 'dark' && this.currentCompany.logoDark) {
        return this.currentCompany.logoDark
      }
      return this.currentCompany.logo || ''
    },
    // hidden entirely when the browser can't push or the deployment has no
    // VAPID keys (feature off server-side)
    showWebPushToggle () {
      return this.webPushState !== 'unsupported' && !!this.currentCompany.vapidPublicKey
    }
    //  currentCompany () {
    // return this.$store.getters.company
    // }
  },
  watch: {
    // subscription + service worker outlive the 15-minute session: re-post
    // the registration on every login so lastseen refreshes and a row the
    // 410-cleanup removed gets resurrected
    'client.authenticated' (authenticated) {
      if (authenticated) {
        this.resyncWebPushRegistration()
      }
    }
  },
  mounted () {
    this.loadCompanyValues()
    /* eslint-disable no-console */
    // console.log('Page Header')
    /* eslint-disable no-console */
    // console.log(this.$store)
    if (!this.client.authenticated) {
      this.$store.dispatch('getClientData')
    } else {
      this.resyncWebPushRegistration()
    }
    this.updateWebPushState()
  },
  methods: {
    loadCompanyValues () {
      this.$store.dispatch('getCompanyData')
        .then(() => {
          this.currentCompany = this.$store.getters.company
        })
        .catch(() => {
          /* eslint-disable no-console */
          console.log('could not get company data')
        })
    },
    routeTo (path, params) {
      const absolutePath = (path === 'home') ? '/' : '/' + path
      if (this.$route.path !== absolutePath) {
        const argument = { name: path }
        if (params) {
          argument.params = params
        }
        this.$router.push(argument)
      }
    },
    goHome () {
      this.routeTo('home')
    },
    organizationManagement () {
      this.routeTo('organizations')
    },
    facilityManagement () {
      const params = { organizationid: this.client.organization.id, organization: this.client.organization }
      this.routeTo('facilities', params)
      // this.$router.push({ name: 'facilities', params: { organizationid: this.client.organization.id, organization: this.client.organization } })
    },
    userManagement () {
      this.routeTo('users')
    },
    collectionAddressManagement () {
      this.routeTo('collections')
    },
    anomaliesView () {
      this.routeTo('anomalies')
    },
    apiTokenManagement () {
      this.routeTo('apitokens')
    },
    emissionCoverageView () {
      this.routeTo('emissioncoverage')
    },
    selectOrganization (organization) {
      this.loginWithOrganization(organization, this.client.name)
    },

    // ---- Web Push (browser notifications) ---------------------------

    async updateWebPushState () {
      this.webPushState = await webPush.getState()
    },
    webPushRegistrationBody (fields) {
      return {
        id: 0, // ignored by /refreshpushtoken, but @NotNull in the payload
        userid: this.client.userid,
        lastseen: new Date().toISOString(),
        vendor: 'WEBPUSH',
        model: webPush.browserModel(),
        token: fields.endpoint,
        p256dh: fields.p256dh,
        auth: fields.auth,
        silentdelivery: 0,
        developermode: false,
        appvendorid: webPush.getInstallId()
      }
    },
    async toggleWebPush () {
      if (this.webPushBusy) return
      this.webPushBusy = true
      try {
        if (this.webPushState === 'subscribed') {
          await this.disableWebPush()
        } else {
          await this.enableWebPush()
        }
      } catch (e) {
        /* eslint-disable no-console */
        console.log('web push toggle failed', e)
      } finally {
        this.webPushBusy = false
        this.updateWebPushState()
      }
    },
    async enableWebPush () {
      const fields = await webPush.subscribe(this.currentCompany.vapidPublicKey)
      await usersService.refreshUserPushToken(this.webPushRegistrationBody(fields))
    },
    async disableWebPush () {
      // delete the server row first (needs the endpoint), then unsubscribe.
      // Authenticated DELETE by id — the public /{token}/{appVendorId} route
      // can't carry an endpoint URL (slashes) as a path variable.
      const subscription = await webPush.getSubscription()
      if (subscription) {
        try {
          const data = await usersService.getUserPushTokensData(this.client.userid)
          const rows = (data && data.userpushtokens) || []
          const row = rows.find(r => r.vendor === 'WEBPUSH' && r.token === subscription.endpoint)
          if (row) {
            await usersService.deleteUserPushToken(row.id)
          }
        } catch (e) {
          /* eslint-disable no-console */
          console.log('web push server row cleanup failed', e)
        }
      }
      await webPush.unsubscribe()
    },
    async resyncWebPushRegistration () {
      try {
        if (!webPush.isSupported() || Notification.permission !== 'granted') return
        const subscription = await webPush.getSubscription()
        if (!subscription || !this.client.userid) return
        const fields = webPush.subscriptionFields(subscription)
        const body = this.webPushRegistrationBody(fields)
        // keep an admin-set severity threshold — the resync must not reset it
        try {
          const data = await usersService.getUserPushTokensData(this.client.userid)
          const row = ((data && data.userpushtokens) || []).find(r => r.vendor === 'WEBPUSH' && r.token === fields.endpoint)
          if (row && row.silentdelivery != null) {
            body.silentdelivery = row.silentdelivery
          }
        } catch (e) {
          // list not readable — register with the default threshold
        }
        await usersService.refreshUserPushToken(body)
      } catch (e) {
        // silent — resync is best-effort, the explicit toggle stays the source of truth
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.page-header {
  padding: 9px 10px 8px;
  border-bottom: 1px solid #eee;
  margin-bottom: 10px;

  .header-section {
    flex: 1;
    display: flex;
    align-items: center;
  }

  .header-left {
    justify-content: flex-start;
  }

  .header-center {
    justify-content: center;
  }

  .header-right {
    justify-content: flex-end;
  }

  .logo {
    color: #444;
    cursor: pointer;
    display: flex;
    align-items: center;

    .home-icon {
      font-size: 20px;
    }
  }

  .header-btn {
    color: #444;
    font-size: 14px;
    padding: 4px 8px;
    text-decoration: none;

    &:hover, &:focus {
      color: #000;
      box-shadow: none;
    }
  }

  .selected-menu {
    background-color: #b8daff;
  }
}

.dropdown-toggle {
  padding: 2px 5px !important;
}
</style>
